import { customElement } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ContextChangeController } from './context-change.js';
import { nsSettingsServiceContext, ltnAuthServiceContext, ltnAuthStateContext } from './contexts.js';

import { LitElement } from 'lit';
import { ButtressDbService, QueryResult, buttressDbServiceContext } from '@buttress/crag';
import { AuthState, LtnAuthService } from '../ltn-auth/ltn-auth-service.js';

import { NSSettingsService } from '../ns-settings/ns-settings-service.js';

import Person from '../models/person.js';
import Company from '../models/company.js';
import { createLogger } from './logger.js';
import { awaitDbConnection } from '../helpers/db-connection.js';
import { ButtressSubscriptions } from '../helpers/buttress-subscriptions.js';
import { Permission, RoleAssignmentRow, RoleRow, permissionsFrom } from '../helpers/permissions.js';

const logger = createLogger('directory-service');

interface ActiveProfileMessage {
  userId: string | null;
  profileId: string;
}

export interface menuItem {
  id: string;
  title: string;
  icon: string;
  link: string;
  fn?: Function;
  badgeCount?: number;
}

@customElement('directory-service')
export class NSDirectoryService extends LitElement {
  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;
  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;
  private __authChanges = new ContextChangeController(this, ltnAuthStateContext, ({ state }) =>
    this.__authStateChanged(state),
  );
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  private __initialised: boolean = false;
  private __initialisationQueue: Function[] = [];

  private __people?: Person[];
  private __me?: Person;
  private __company?: Company;
  private __permissions = new Set<Permission>();
  private __activeProfileChannel?: BroadcastChannel;

  private readonly __subscriptions = new ButtressSubscriptions(this, () => this.__db);

  // crag also fires it once during start-up, which __initDirectory already covers
  private readonly __reloadAfterResync = async () => {
    if (!this.__initialised) return;
    await this.__onPeopleChanged();
    await this.__loadCompany();
    await this.__loadPermissions();
    this.dispatchEvent(new CustomEvent('company-change'));
  };

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__settings) throw new Error('Unable to find settings service');
    // TODO: On settings change to companyId, call __loadCompany.

    if (!this.__db) {
      throw new Error('A DB Service Is Required!');
    }

    if (!this.__auth) {
      throw new Error('Auth service is required!');
    }

    this.__subscriptions.subscribe('people.*', () => this.__onPeopleChanged());
    this.__subscriptions.subscribe('company.*', (cr: { path?: string }) => this.__onCompanyChanged(cr));
    this.__subscriptions.subscribe('role.*', () => this.__onRolesChanged());
    this.__subscriptions.subscribe('roleAssignment.*', () => this.__onRolesChanged());
    this.__db.addEventListener('bjs-resync', this.__reloadAfterResync);

    // crag drops realtime activity made by the same user, so other tabs never hear of a switch through it
    this.__activeProfileChannel = new BroadcastChannel('ns-active-profile');
    this.__activeProfileChannel.addEventListener('message', (ev: MessageEvent<ActiveProfileMessage>) =>
      this.__onActiveProfileSwitched(ev.data),
    );
  }

  disconnectedCallback() {
    this.__activeProfileChannel?.close();
    this.__db?.removeEventListener('bjs-resync', this.__reloadAfterResync);
    super.disconnectedCallback();
  }

  public get initialised() {
    if (this.__initialised) return true;
    const p = new Promise<boolean>((resolve) => this.__initialisationQueue.push(resolve));
    return p;
  }

  public get me(): Person | undefined {
    if (!this.__me) return;
    return this.__me;
  }

  public get people(): Person[] {
    return this.__people ?? [];
  }

  public get company(): Company | undefined {
    if (!this.__company) return;
    return this.__company;
  }

  // Shows or hides a control; the server still decides
  public can(permission: Permission): boolean {
    return this.__permissions.has(permission);
  }

  /**
   * @method __authStateChanged
   *
   * @param state
   * @returns
   */
  private async __authStateChanged(_state: AuthState) {
    if (!this.__auth?.token) {
      return;
    }

    logger.debug('Initialising...');
    try {
      await this.__initDirectory();
    } catch (err) {
      // Nothing awaits this callback, and every view waits on `initialised`, so the app must be told
      logger.error('Unable to initialise the directory', err);
      this.dispatchEvent(new CustomEvent('initialise-failed'));
      return;
    }

    this.__initialised = true;
    this.__initialisationQueue.forEach((fn) => fn(true));
    this.dispatchEvent(new CustomEvent('initialised', { detail: this.__me }));
  }

  /**
   * @method __initDirectory
   *
   * @returns void
   */
  private async __initDirectory() {
    if (!this.__auth || !this.__db) return;
    await awaitDbConnection(this.__db);

    await this.__reloadPeople();
    await this.__loadCompany();
    await this.__loadPermissions();
  }

  private async __onPeopleChanged() {
    if (!this.__me) return;
    if (!(await this.__fetchPeople())) return;

    this.__me = this.people.find((p) => p.id === this.__me?.id) ?? this.__me;
    await this.__loadPermissions();
    // After the refetch, so a listener reads the new profile rather than racing it on its own people.* subscription
    this.dispatchEvent(new CustomEvent('me-updated'));
  }

  private async __onRolesChanged() {
    if (!this.__me) return;
    await this.__loadPermissions();
    this.dispatchEvent(new CustomEvent('me-updated'));
  }

  // The caller's own assignments and their company's roles, both of which the user policy lets them read
  private async __loadPermissions() {
    const me = this.__me;
    if (!me?.companyId || !this.__db) {
      this.__permissions = new Set();
      return;
    }
    const assignments = (await this.__db.query('roleAssignment', { personId: { $eq: me.id } }))
      .results as unknown as RoleAssignmentRow[];
    const roleIds = [...new Set(assignments.map((a) => a.roleId).filter((id): id is string => !!id))];
    const roles =
      roleIds.length > 0
        ? ((await this.__db.query('role', { id: { $in: roleIds } })).results as unknown as RoleRow[])
        : [];
    this.__permissions = permissionsFrom(me, assignments, roles, this.__company?.features);
  }

  private async __onCompanyChanged(cr: { path?: string }) {
    if (!this.__me?.companyId || cr.path?.split('.')[1] !== this.__me.companyId) return;

    await this.__loadCompany();
    // A feature turned on or off changes what the caller may do there
    await this.__loadPermissions();
    this.dispatchEvent(new CustomEvent('company-change'));
    this.dispatchEvent(new CustomEvent('me-updated'));
  }

  private __onActiveProfileSwitched({ userId, profileId }: ActiveProfileMessage) {
    if (!this.__me || this.__me.userId !== userId || this.__me.id === profileId) return;

    // This tab's store and the server's scope now disagree: the app reloads it behind the shutter
    // (ns-app's listener), or straight away when nothing is listening
    const handled = !this.dispatchEvent(new CustomEvent('active-profile-switched', { cancelable: true }));
    if (!handled) window.location.reload();
  }

  /**
   * @method __reloadPeople
   *
   * @returns void
   */
  private async __reloadPeople() {
    if (!(await this.__fetchPeople())) return;
    await this.__initPerson();
  }

  private async __fetchPeople(): Promise<boolean> {
    const userId = this.__auth?.user?.id;
    if (!userId || !this.__db) return false;
    const queryCall: QueryResult | undefined = await this.__db.query('people', {
      userId: {
        $eq: userId,
      },
    });

    if (!queryCall || queryCall.results.length === 0) {
      throw new Error(`Unable to find any profiles for user ${userId}`);
    }

    this.__people = queryCall.results as unknown[] as Person[];
    return true;
  }

  /**
   * @method __initPerson
   *
   * @returns void
   */
  private async __initPerson() {
    if (!this.__people) throw new Error('Missing required people array to init directory person');

    const candidates = this.__people.filter((p) => p.activeProfile !== false);
    const activeProfile = candidates[0] ?? this.__people[0];
    if (candidates.length !== 1) await this.__activateProfile(activeProfile);

    this.__me = activeProfile;
  }

  // The user-access policy resolves #env.companyId from whichever profile this leaves active
  private async __activateProfile(profile: Person) {
    if (!this.__db) throw new Error('Missing required db service');

    const needsActivating = profile.activeProfile !== true;
    const deactivations = this.people.filter((p) => p.id !== profile.id && p.activeProfile !== false);
    if (!needsActivating && deactivations.length === 0) return;

    if (needsActivating) this.__db.set(`people.${profile.id}.activeProfile`, true);
    deactivations.forEach((p) => this.__db?.set(`people.${p.id}.activeProfile`, false));
    await this.__db.nextIdle('people');

    this.__activeProfileChannel?.postMessage({ userId: profile.userId, profileId: profile.id });
  }

  /**
   * @method __loadCompany
   *
   * @returns void
   */
  private async __loadCompany() {
    if (!this.__me || !this.__me.companyId || !this.__db) return;
    // A company the user can't read rejects; without this catch the directory never finishes initialising.
    const company = await this.__db.getById<Company>('company', this.__me.companyId).catch(() => undefined);
    if (!company) {
      logger.warn(`Unable to find any companies for user ${this.__me.id} and company ID: ${this.__me.companyId}`);
      this.__company = undefined;
      return;
    }

    this.__company = company;
  }

  async selectCompanyProfile(companyId: string) {
    if (!companyId) throw new Error('Can not select company profile without a company ID');

    await this.__fetchPeople();
    const profile = this.people.find((p) => p.companyId === companyId);
    if (!profile) throw new Error(`Unable to find a profile at company ${companyId}`);

    await this.__activateProfile(profile);
    this.__me = profile;
    await this.__loadCompany();
    await this.__loadPermissions();

    this.dispatchEvent(new CustomEvent('profile-change', { detail: true }));
  }
}
